import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './side/Sidebar';
import '../css/UserManagement.css';
import { SERVER_URL } from '../../config/config';
import Pagination from './side/Pagination';
import ErrorPopup from './side/ErrorPopup';
import ConfirmPopup from './side/ConfirmPopup';
import TopHeader from './side/TopHeader';

const AdminManagement = () => {
  // admins => 승인된 관리자, pendingAdmins => 승인 대기 관리자
  const [admins, setAdmins] = useState([]);
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [currentAdminPage, setCurrentAdminPage] = useState(1);
  const [currentPendingPage, setCurrentPendingPage] = useState(1);
  const [totalAdminPages, setTotalAdminPages] = useState(1);
  const [totalPendingPages, setTotalPendingPages] = useState(1);
  const ITEMS_PER_PAGE = 5; // 한 페이지당 10개의 항목 표시
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorPopupMessage, setErrorPopupMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [selectedAdminName, setSelectedAdminName] = useState('');
  const [isRejectAction, setIsRejectAction] = useState(false);
  const [isApproveAction, setIsApproveAction] = useState(false);
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${SERVER_URL}/admin/admin`);
      
      if (response.data.result_code === 200) {
        // signup_date가 null이 아닌 관리자는 승인된 관리자
        const approved = response.data.admins.filter(admin => admin.signup_date !== null);
        // signup_date가 null인 관리자는 승인 대기 관리자
        const pending = response.data.admins.filter(admin => admin.signup_date === null);
        
        setAdmins(approved);
        setPendingAdmins(pending);
        setTotalAdminPages(Math.ceil(approved.length / ITEMS_PER_PAGE));
        setTotalPendingPages(Math.ceil(pending.length / ITEMS_PER_PAGE));
      } else {
        setError('Failed to fetch admin data');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Failed to fetch admin data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const getCurrentPageItems = (items, currentPage) => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const handleAdminPageChange = (page) => {
    setCurrentAdminPage(page);
  };

  const handlePendingPageChange = (page) => {
    setCurrentPendingPage(page);
  };

  // 2. 관리자 탈퇴 기능
  const handleRemoveAdmin = async (adminId) => {
    try {
      const response = await axios.delete(`${SERVER_URL}/admin/admin/delete`, {
        params: {
          login_id: localStorage.getItem('adminId'),
          admin_id: Number(adminId)
        }
      });
      
      if (response.data.result_code === 200) {
        setAdmins(admins.filter(admin => admin.admin_id !== adminId));
        setTotalAdminPages(Math.ceil((admins.length - 1) / ITEMS_PER_PAGE));
        if (currentAdminPage > Math.ceil((admins.length - 1) / ITEMS_PER_PAGE)) {
          setCurrentAdminPage(Math.ceil((admins.length - 1) / ITEMS_PER_PAGE));
        }
        setErrorPopupMessage(response.data.message);
        setShowErrorPopup(true);
      } else {
        console.error('Failed to remove admin:', response.data);
        setErrorPopupMessage(response.data.message || '관리자 탈퇴 처리에 실패했습니다.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error removing admin:', error);
      const errorMessage = error.response?.data?.message || '관리자 탈퇴 처리 중 오류가 발생했습니다.';
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    }
  };

  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorPopupMessage('');
  };

  const handleAdminApprove = async (adminId, role) => {
    try {
      const response = await axios.post(`${SERVER_URL}/admin/admin/accept`, null, {
        params: {
          login_id: localStorage.getItem('adminId'),
          admin_id: Number(adminId),
          role: role
        }
      });
      if (response.data.result_code === 200) {
        await fetchAdmins();
        setErrorPopupMessage(response.data.message);
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error approving admin:', error);
      const errorMessage = error.response?.data?.message || '관리자 승인 중 오류가 발생했습니다.';
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    }
  };

  const handleAdminReject = async (adminId) => {
    try {
      const response = await axios.delete(`${SERVER_URL}/admin/admin/delete`, {
        params: {
          login_id: localStorage.getItem('adminId'),
          admin_id: adminId
        }
      });
      if (response.data.result_code === 200) {
        await fetchAdmins(); // 거절 후 목록 새로고침
        setErrorPopupMessage(response.data.message);
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error rejecting admin:', error);
      const errorMessage = error.response?.data?.message || '관리자 거절 중 오류가 발생했습니다.';
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    }
  };

  const handleRemoveClick = (adminId, name) => {
    setSelectedAdminId(adminId);
    setSelectedAdminName(name);
    setIsRejectAction(false);
    setShowConfirmPopup(true);
  };

  const handleRejectClick = (adminId, name) => {
    setSelectedAdminId(adminId);
    setSelectedAdminName(name);
    setIsRejectAction(true);
    setShowConfirmPopup(true);
  };

  const handleApproveClick = (adminId, name) => {
    setSelectedAdminId(adminId);
    setSelectedAdminName(name);
    setIsApproveAction(true);
    setIsRejectAction(false);
    setShowRoleSelectionModal(true);
  };

  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    setShowRoleSelectionModal(false);
    setShowConfirmPopup(true);
  };

  const handleConfirmAction = async () => {
    if (isApproveAction) {
      await handleAdminApprove(selectedAdminId, selectedRole);
    } else if (isRejectAction) {
      await handleAdminReject(selectedAdminId);
    } else {
      await handleRemoveAdmin(selectedAdminId);
    }
    setShowConfirmPopup(false);
    setShowRoleSelectionModal(false);
    setSelectedAdminId(null);
    setSelectedAdminName('');
    setSelectedRole('');
    setIsApproveAction(false);
    setIsRejectAction(false);
  };

  const handleCancelAction = () => {
    setShowConfirmPopup(false);
    setShowRoleSelectionModal(false);
    setSelectedAdminId(null);
    setSelectedAdminName('');
    setSelectedRole('');
    setIsApproveAction(false);
    setIsRejectAction(false);
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="content-wrapper">
          <Sidebar />
          <div className="main-content">
            <div className="loading">로딩 중...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management">
        <div className="content-wrapper">
          <Sidebar />
          <div className="main-content">
            <div className="error">{error}</div>
            <button onClick={fetchAdmins} className="delete-button">다시 시도</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="content-wrapper">
        <Sidebar />
        <div className="main-content">
          <div className="header-section">
            <h1>관리자 목록 ({admins.length}명)</h1>
          </div>

          <div className="table-container">
            <div className="header-row" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
              <span>가입일</span>
              <span>이름</span>
              <span>아이디</span>
              <span>생년월일</span>
              <span>역할</span>
              <span>관리</span>
            </div>
            <div className="data-entries">
              {getCurrentPageItems(admins, currentAdminPage).map((admin) => (
                <div className="table-row" key={admin.admin_id} style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
                  <span className="cell">{admin.signup_date}</span>
                  <span className="cell">{admin.name}</span>
                  <span className="cell">{admin.username}</span>
                  <span className="cell">{admin.birth}</span>
                  <span className="cell">
                    {admin.role === 'AI' ? 'AI관리자' : 
                     admin.role === 'USER' ? '사용자 관리자' : 
                     admin.role === 'MASTER' ? '마스터 관리자' : 
                     admin.role}
                  </span>
                  <span className="cell">
                    <button 
                      className="delete-button"
                      onClick={() => handleRemoveClick(admin.admin_id, admin.name)}
                    >
                      관리자 탈퇴
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            currentPage={currentAdminPage}
            totalPages={totalAdminPages}
            onPageChange={handleAdminPageChange}
          />

          <div className="header-section" style={{ marginTop: '40px' }}>
            <h1>승인 대기 ({pendingAdmins.length}명)</h1>
          </div>

          <div className="table-container">
            <div className="header-row">
              <span>이름</span>
              <span>아이디</span>
              <span>생년월일</span>
              <span>관리</span>
            </div>
            <div className="data-entries">
              {getCurrentPageItems(pendingAdmins, currentPendingPage).map((admin) => (
                <div className="table-row" key={admin.username} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  <span className="cell">{admin.name}</span>
                  <span className="cell">{admin.username}</span>
                  <span className="cell">{admin.birth}</span>
                  <span className="cell">
                    <button 
                      className="delete-button"
                      style={{ backgroundColor: '#4CAF50', marginRight: '8px' }}
                      onClick={() => handleApproveClick(admin.admin_id, admin.name)}
                    >
                      승인
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleRejectClick(admin.admin_id, admin.name)}
                    >
                      거절
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            currentPage={currentPendingPage}
            totalPages={totalPendingPages}
            onPageChange={handlePendingPageChange}
          />
        </div>
      </div>

      {showErrorPopup && (
        <ErrorPopup
          message={errorPopupMessage}
          onClose={handleCloseErrorPopup}
        />
      )}

      {showRoleSelectionModal && (
        <div className="admin-role-modal-overlay">
          <div className="admin-role-modal">
            <h3 className="admin-role-modal-title">관리자 역할 선택</h3>
            <p className="admin-role-modal-desc">{selectedAdminName}님의 관리자 역할을 선택해주세요.</p>
            <div className="admin-role-buttons">
              <button 
                className="admin-role-button user"
                onClick={() => handleRoleSelection('USER')}
              >
                사용자 관리자
              </button>
              <button 
                className="admin-role-button ai"
                onClick={() => handleRoleSelection('AI')}
              > 
                AI 모델 관리자
              </button>
            </div>
            <button 
              className="admin-role-button cancel"
              onClick={() => {
                setShowRoleSelectionModal(false);
                setSelectedAdminId(null);
                setSelectedAdminName('');
              }}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {showConfirmPopup && (
        <ConfirmPopup
          message={isApproveAction 
            ? `정말 ${selectedAdminName}님을 ${selectedRole === 'USER' ? '사용자 관리자' : 'AI 모델 관리자'}로 승인하시겠습니까?`
            : isRejectAction 
              ? `정말 ${selectedAdminName}님의 가입 승인을 거절하시겠습니까?`
              : `정말 ${selectedAdminName}님의 관리자탈퇴를 진행하시겠습니까?`}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
        />
      )}
    </div>
  );
};

export default AdminManagement; 
